'use client'

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

import { preconnect, preload } from 'react-dom'
import type Hls from 'hls.js'
import type { HlsConfig } from 'hls.js'

import { resolveVideoSrc } from '@/lib/image-src'
import { cn } from '@/lib/utils'

/**
 * Үндсэн слайдерын ВИДЕО слайд.
 *
 * CMS-ийн нээлттэй JSON-оос (hero `items[]`) уншина:
 *  - `mediaType: 'video'` — үгүй бол энгийн зургийн слайд,
 *  - `hlsUrl` (master.m3u8) → эхлээд, `mp4Url` / `videoUrl` → нөөц,
 *  - `posterUrl` (эсвэл хуучин `image`) → видео ачаалагдтал харагдах зураг,
 *  - `playbackRate` (анхдагч 0.5), `overlay` (анхдагч асаалттай),
 *    `wait_for_end` (админы «Видео дуустал солихгүй», анхдагч асаалттай).
 *
 * ! Талбарын нэрийг camelCase БА snake_case хоёуланг нь уншина — CMS-ийн
 * ! админ snake_case (`media_type`, `playback_rate`, `wait_for_end`)
 * ! хадгалдаг, нээлттэй API нь camelCase (`hlsUrl`, `mp4Url`) нэмдэг.
 * ! Гэрээ: cloud.maximus.mn `apps/api/src/website/website-video-slide.ts`.
 */
export type HeroVideoSource = {
  hls: string | null
  mp4: string | null
  poster: string | null

  /** 0.25–2; анхдагч 0.5 («2 дахин удаан») */
  rate: number

  /** Текстийн доорх бараан градиент */
  overlay: boolean

  /** Дуусмагц дараагийн слайд руу шилжих — үгүй бол давтан тоглоно */
  advanceOnEnd: boolean
}

const DEFAULT_RATE = 0.5

const pick = (slide: Record<string, unknown>, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = slide[key]

    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return null
}

/** `false`, 'false', '0', 'none', 'off', 'no' → унтраалттай; хоосон бол анхдагч */
const flag = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value !== 0
  }

  if (typeof value !== 'string' || !value.trim()) {
    return fallback
  }

  return !['false', '0', 'none', 'off', 'no'].includes(value.trim().toLowerCase())
}

const playbackRate = (value: unknown): number => {
  const rate = Number(value)

  // ! `Number('')` = 0 — хоосон утга анхдагч руу орно
  if (!Number.isFinite(rate) || rate <= 0) {
    return DEFAULT_RATE
  }

  return Math.min(2, Math.max(0.25, rate))
}

/** Слайд видео биш, эсвэл тоглуулах хаяггүй бол null */
export const toHeroVideo = (slide: Record<string, unknown>): HeroVideoSource | null => {
  if (pick(slide, 'mediaType', 'media_type') !== 'video') {
    return null
  }

  const hls = resolveVideoSrc(pick(slide, 'hlsUrl', 'hls_url'))
  const mp4 = resolveVideoSrc(pick(slide, 'mp4Url', 'mp4_url', 'videoUrl', 'video_url', 'video'))

  if (!hls && !mp4) {
    return null
  }

  return {
    hls,
    mp4,
    poster: pick(slide, 'posterUrl', 'poster_url', 'poster', 'image'),
    rate: playbackRate(slide.playbackRate ?? slide.playback_rate),
    overlay: flag(slide.overlay, true),

    // ! CMS `wait_for_end` илгээнэ — `advanceOnEnd` нь зөвхөн хуучин нэр
    advanceOnEnd: flag(slide.waitForEnd ?? slide.wait_for_end ?? slide.advanceOnEnd ?? slide.advance_on_end, true)
  }
}

/**
 * Эхний слайдын видеог `<head>`-д зарлана: медиа хост руу урьдчилан холбогдож,
 * master.m3u8-ыг HTML задлагдмагц татаж эхэлнэ — JS, hydration-ыг хүлээхгүй.
 * Render дотор дуудна; SSR-ийн үед React `<link>`-ийг `<head>`-д гаргана.
 *
 * ! `as: 'fetch'` + `crossOrigin: 'anonymous'` нь hls.js-ийн XHR (CORS,
 * ! күүкигүй)-тэй ЯГ ТААРАХ ёстой — эс бөгөөс хөтөч урьдчилан татсанаа
 * ! ашиглахгүй, дахин татна. Safari-ийн native тоглуулагч master-ийг өөрөө
 * ! дахин татаж магадгүй — ~300 байт тул хохиролгүй.
 */
export const hintHeroVideo = (source: HeroVideoSource | null) => {
  if (!source?.hls) return

  if (/^https:\/\//i.test(source.hls)) preconnect(new URL(source.hls).origin, { crossOrigin: 'anonymous' })

  preload(source.hls, { as: 'fetch', crossOrigin: 'anonymous' })
}

/* ------------------------------------------------------------------------ */

const REDUCE_MOTION = '(prefers-reduced-motion: reduce)'

type NetworkInformationLike = EventTarget & { saveData?: boolean; effectiveType?: string }

const connection = () => (navigator as Navigator & { connection?: NetworkInformationLike }).connection

const subscribeVideoAllowed = (onChange: () => void) => {
  const query = window.matchMedia(REDUCE_MOTION)
  const network = connection()

  query.addEventListener('change', onChange)
  network?.addEventListener?.('change', onChange)

  return () => {
    query.removeEventListener('change', onChange)
    network?.removeEventListener?.('change', onChange)
  }
}

const videoAllowed = () => {
  if (window.matchMedia(REDUCE_MOTION).matches) {
    return false
  }

  // Зөвхөн Chromium мэддэг — Safari/Firefox дээр үргэлж зөвшөөрнө
  const network = connection()

  return !(network?.saveData || network?.effectiveType === '2g' || network?.effectiveType === 'slow-2g')
}

/**
 * Видео автоматаар тоглуулж болох эсэх.
 *
 * ! Серверт болон hydration-ий үед ҮРГЭЛЖ false — видео зөвхөн клиент
 * ! дээр, хэрэглэгчийн тохиргоог (хөдөлгөөн багасгах, дата хэмнэх) мэдсэний
 * ! дараа холбогдоно. Тэр хүртэл постер харагдана.
 */
export const useVideoAllowed = () => useSyncExternalStore(subscribeVideoAllowed, videoAllowed, () => false)

const subscribeVisibility = (onChange: () => void) => {
  document.addEventListener('visibilitychange', onChange)

  return () => document.removeEventListener('visibilitychange', onChange)
}

/** Таб харагдаж байгаа эсэх — нуугдсан табд видео дуусаж, слайд үл үзэгдэн шилжихгүй */
export const usePageVisible = () =>
  useSyncExternalStore(
    subscribeVisibility,
    () => document.visibilityState !== 'hidden',
    () => true
  )

/* ------------------------------------------------------------------------ */

/**
 * Слайдерын хүсэж буй төлөв:
 *  - `enabled` — видео зөвшөөрөгдсөн (хөдөлгөөн багасгах / дата хэмнэх биш),
 *  - `selected` — сонгогдсон слайд; үгүй бол зогсоож эхэнд нь буцаана,
 *  - `preload` — дараагийн слайд; манифест/metadata-г урьдчилан ачаална,
 *  - `paused` — түр зогсоосон (дэлгэцээс гарсан, таб нуугдсан); байрлалаа хадгална.
 */
type Want = {
  enabled: boolean
  selected: boolean
  preload: boolean
  paused: boolean
}

type Hooks = {
  onPlaying: () => void
  onEnded: () => void
  onFail: () => void
}

const HLS_MIME = 'application/vnd.apple.mpegurl'

/**
 * Native HLS-ийг ЭХЛЭЭД оролдох эсэх: Apple-ийн WebKit (Safari, iOS дээрх бүх
 * хөтөч) эсвэл MSE-гүй хөтөч (hls.js ажиллахгүй). Бусад нь hls.js-ээр.
 *
 * ! Chrome 142+ (desktop) ба Android Chrome ч `canPlayType`-д «maybe» гэдэг.
 * ! Зөвхөн түүгээр шийдвэл тэд native руу орж, доорх эхлэх түвшин, буферийн
 * ! тохиргоо ОГТ үйлчлэхгүй.
 */
const nativeHlsFirst = () =>
  navigator.vendor.startsWith('Apple') || !('MediaSource' in window || 'ManagedMediaSource' in window)

/**
 * ! Дуугүй VOD слайдад буфер бүү хуримтлуул. Анхдагч нь `backBufferLength` =
 * ! Infinity, `maxBufferLength` = 30 сек (0.5×-д бодитоор 60 сек урагш).
 *
 * ! `maxBufferLength` нь ДООД хязгаар — hls.js түүнийг `maxBufferSize` (60 МБ) /
 * ! битрейтээр сунгадаг (480p-д ~300 сек, өөрөөр хэлбэл бүх клип). Бодит дээд
 * ! хязгаар нь `maxMaxBufferLength`. Урагш 8 сек (0.5×-д 16 сек) хангалттай;
 * ! их буфер бол хамгийн бага түвшинд эхэлж татсан хэсгүүд тэр чигээрээ
 * ! тоглогдож, ABR чанарыг өсгөж амжихгүй.
 */
const HLS_CONFIG: Partial<HlsConfig> = {
  autoStartLoad: false,
  capLevelToPlayerSize: true,
  backBufferLength: 10,
  maxBufferLength: 8,
  maxMaxBufferLength: 8
}

let hlsJsModule: Promise<{ default: typeof Hls }> | null = null

/**
 * `hls.js/light`-ийг НЭГ удаа татна — бүх видео слайд нэг Promise-ийг хуваалцана.
 *
 * ! `hls.js/light` — EME, хадмал, нэмэлт дуу, CMCD-гүй (~115 КБ gzip,
 * ! бүтэн нь ~180 КБ). Дуугүй VOD слайдад тэдгээр хэрэггүй.
 */
const loadHlsJs = () =>
  (hlsJsModule ??= import('hls.js/light').catch((error: unknown) => {
    // ! Сүлжээний түр алдааг бүү хадгал — дараагийн оролдлого дахин татна
    hlsJsModule = null

    throw error
  }))

/*
 * ! Модуль ачаалагдмагц ТАТАЖ ЭХЭЛНЭ. Өмнө нь effect дотор — hydration,
 * ! `useVideoAllowed`-ийн дахин зурагдалтын ДАРАА — эхэлдэг байсан тул ~115 КБ
 * ! чанк манифест, эхний хэсгээс өмнө дараалалд зогсдог байв. Native HLS
 * ! (Safari) болон видео хориотой үед татахгүй — хэрэг гарвал `startHlsJs` татна.
 */
if (typeof window !== 'undefined' && videoAllowed() && !nativeHlsFirst()) loadHlsJs().catch(() => {})

/** Энэ хугацаанд явц ахихгүй бол: эхлээгүй → зураг руу, эхэлсэн → дараагийн слайд */
const STALL_MS = 8000

/** Гарч буй слайдыг шилжилтийн хөдөлгөөн дууссаны дараа эхэнд нь буцаана */
const RESET_DELAY_MS = 800

/**
 * `<video>`-г React-аас гадуур, нэг газраас удирдана.
 *
 * Эх сурвалжийн дараалал: native HLS (Safari/iOS, `nativeHlsFirst`) → hls.js
 * (`loadHlsJs`) → mp4. Native алдаа өгвөл hls.js-ийг оролдоно — шууд эх mp4
 * (12 Мбит/с) руу бүү үсэр. Аль нь ч бүтэхгүй, эсвэл хөтөч автомат
 * тоглуулалтыг хориглосон бол `onFail` — слайдер тэр слайдыг зураг гэж үзнэ.
 */
const createVideoController = (
  video: HTMLVideoElement,
  source: Pick<HeroVideoSource, 'hls' | 'mp4' | 'rate'>,
  want: () => Want,
  hooks: Hooks
) => {
  let mode: 'idle' | 'pending' | 'native' | 'hlsjs' | 'mp4' | 'failed' = 'idle'
  let hls: Hls | null = null
  let triedHlsJs = false
  let manifestReady = false
  let loading = false
  let watchdog = 0
  let resetTimer = 0
  let disposed = false

  const applyRate = () => {
    video.defaultPlaybackRate = source.rate
    video.playbackRate = source.rate
  }

  const clearWatchdog = () => {
    window.clearTimeout(watchdog)
    watchdog = 0
  }

  const armWatchdog = () => {
    clearWatchdog()
    watchdog = window.setTimeout(() => {
      watchdog = 0

      if (video.currentTime > 1) {
        hooks.onEnded()
      } else {
        fail()
      }
    }, STALL_MS)
  }

  const cancelReset = () => {
    window.clearTimeout(resetTimer)
    resetTimer = 0
  }

  const dropHls = () => {
    hls?.destroy()
    hls = null
    manifestReady = false
    loading = false
  }

  /** hls.js-ийн MediaSource, буферийг (1080p-д ~9 МБ) чөлөөлнө — дахин сонгогдвол шинээр холбоно */
  const release = () => {
    dropHls()
    mode = 'idle'
    video.removeAttribute('src')
    video.load()
  }

  /**
   * Гарч буй слайд: шилжилтийн дараа эхэнд нь буцаана.
   *
   * ! hls.js-тэйг дараагийн слайд биш бол бүр ЧӨЛӨӨЛНӨ — эс бөгөөс үзсэн
   * ! видео слайд бүр MediaSource-оо барьсаар үлдэнэ.
   */
  const scheduleReset = () => {
    if (resetTimer) return

    resetTimer = window.setTimeout(() => {
      resetTimer = 0

      const { selected, preload } = want()

      if (selected) return

      if (mode === 'hlsjs' && !preload) {
        release()
      } else if (video.currentTime !== 0) {
        video.currentTime = 0
      }
    }, RESET_DELAY_MS)
  }

  const fail = () => {
    if (mode === 'failed') return

    mode = 'failed'
    clearWatchdog()
    dropHls()
    video.pause()
    hooks.onFail()
  }

  const toMp4 = (): boolean => {
    if (!source.mp4 || mode === 'mp4' || mode === 'failed') return false

    dropHls()
    mode = 'mp4'
    video.src = source.mp4

    return true
  }

  /** HLS бүтэхгүй бол mp4 руу, тэр нь ч байхгүй бол бүтэлгүйтэл */
  const recover = () => {
    if (toMp4()) {
      // ! Шинэ эх сурвалжид бүтэн хугацаа — өмнөхийн хүлээлтийг бүү үргэлжлүүл
      clearWatchdog()
      sync()
    } else {
      fail()
    }
  }

  /** hls.js-ийг (модуль ачаалахад эхэлсэн татан авалтаас) холбоно; MSE-гүй эсвэл ачаалж чадахгүй бол mp4 */
  const startHlsJs = (url: string) => {
    triedHlsJs = true
    mode = 'pending'
    loadHlsJs().then(
      ({ default: HlsJs }) => {
        if (disposed || mode !== 'pending') return

        if (!HlsJs.isSupported()) return recover()

        const instance = new HlsJs(HLS_CONFIG)

        hls = instance
        mode = 'hlsjs'
        instance.on(HlsJs.Events.MANIFEST_PARSED, (_event, { levels }) => {
          /*
           * ! ХАМГИЙН БАГА битрейтээс эхэлнэ. Анхдагчаар hls.js эхлэх хурдаа
           * ! master-ийн ЭХНИЙ хувилбараас (энд 1080p · 5.5 Мбит/с) тааварлаж
           * ! 720p/1080p-ээс эхэлдэг — эхний 4 сек нь 0.9–2.2 МБ, 480p-д 0.5 МБ.
           * ! Дараагийн хэсгүүдийг ABR бодит хурдаар нь өсгөнө.
           */
          instance.startLevel = levels.reduce((low, level, i) => (level.bitrate < levels[low].bitrate ? i : low), 0)
          manifestReady = true
          sync()
        })
        instance.on(HlsJs.Events.ERROR, (_event, data) => {
          if (data.fatal) recover()
        })
        instance.loadSource(url)
        instance.attachMedia(video)
        sync()
      },
      () => {
        if (!disposed && mode === 'pending') recover()
      }
    )
  }

  /** Эх сурвалжийг холбоно; шууд бүтэлгүйтвэл false */
  const attach = (): boolean => {
    video.muted = true
    video.preload = want().selected ? 'auto' : 'metadata'

    if (!source.hls) {
      if (toMp4()) return true

      fail()

      return false
    }

    if (!triedHlsJs && nativeHlsFirst() && video.canPlayType(HLS_MIME)) {
      mode = 'native'
      video.src = source.hls

      return true
    }

    startHlsJs(source.hls)

    return true
  }

  /** Хүссэн төлөв (`want`) рүү элементийг ойртуулна — хэдэн ч удаа дуудаж болно */
  const sync = () => {
    if (disposed || mode === 'failed') return

    const { enabled, selected, preload, paused } = want()

    if (!enabled) {
      clearWatchdog()
      video.pause()

      return
    }

    if (!selected) {
      clearWatchdog()
      video.pause()

      if (loading) {
        hls?.stopLoad()
        loading = false
      }

      scheduleReset()

      if (preload && mode === 'idle') attach()

      return
    }

    cancelReset()

    if (paused) {
      clearWatchdog()
      video.pause()

      return
    }

    if (mode === 'idle' && !attach()) return

    if (!watchdog) armWatchdog()

    if (mode === 'pending') return

    if (mode === 'hlsjs') {
      if (!manifestReady || !hls) return

      // ! Тоглуулагчийн одоогийн байрлалаас — эхэнд буцаагүй (800мс дотор эргэж ирсэн) ч зөрөхгүй
      if (!loading) {
        hls.startLoad(video.currentTime)
        loading = true
      }
    }

    video.muted = true
    video.preload = 'auto'
    applyRate()
    video.play().catch((error: unknown) => {
      // ! AbortError = pause()/эх сурвалж солих нь тасалсан, алдаа биш.
      // ! NotSupportedError-ийг `error` үзэгдэл хариуцна (mp4 руу шилжинэ).
      if (error instanceof DOMException && error.name === 'NotAllowedError') fail()
    })
  }

  const onPlaying = () => {
    applyRate()
    hooks.onPlaying()
  }

  const onTimeUpdate = () => {
    const { enabled, selected, paused } = want()

    if (enabled && selected && !paused && !video.paused) armWatchdog()
  }

  const onEnded = () => {
    clearWatchdog()
    hooks.onEnded()
  }

  const onError = () => {
    // ! Хуучин эх сурвалжийн хоцорсон үзэгдэл — src солигдоход `error` цэвэрлэгдэнэ
    if (!video.error) return

    // ! hls.js өөрийн ERROR үзэгдлээр мэдэгдэнэ — давхар бүү зохицуул
    if (mode === 'native' && source.hls && !triedHlsJs) {
      // ! Native HLS бүтсэнгүй → hls.js (MSE бий бол), дараа нь л mp4
      clearWatchdog()
      startHlsJs(source.hls)
      sync()
    } else if (mode === 'native') {
      recover()
    } else if (mode === 'mp4') {
      fail()
    }
  }

  video.addEventListener('loadedmetadata', applyRate)
  video.addEventListener('play', applyRate)
  video.addEventListener('playing', onPlaying)
  video.addEventListener('timeupdate', onTimeUpdate)
  video.addEventListener('ended', onEnded)
  video.addEventListener('error', onError)

  return {
    sync,
    destroy: () => {
      disposed = true
      clearWatchdog()
      cancelReset()
      video.removeEventListener('loadedmetadata', applyRate)
      video.removeEventListener('play', applyRate)
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('timeupdate', onTimeUpdate)
      video.removeEventListener('ended', onEnded)
      video.removeEventListener('error', onError)
      dropHls()
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }
}

type HeroVideoProps = Want & {
  source: HeroVideoSource

  /** Ганц слайд, автомат шилжилтгүй, эсвэл `advanceOnEnd` унтраалттай бол давтана */
  loop: boolean
  onEnded: () => void
  onFail: () => void
}

/**
 * Видео давхарга — постер зургийн ДЭЭР, бараан градиентын ДООР байрлана.
 *
 * ! `autoPlay` атрибут ЗОРИУДААР байхгүй: тэгвэл сонгогдоогүй слайд бүр
 * ! зэрэг татагдаж, hydration-оос өмнө 1× хурдтай тоглоно. Тоглуулалтыг
 * ! зөвхөн `play()`-ээр, хурдыг тохируулсны дараа эхлүүлнэ.
 *
 * ! `preload` нь контроллерынхтой ИЖИЛ: сонгогдсон слайд `auto`, дараагийнх
 * ! `metadata`, бусад `none` — эх сурвалжийг контроллер холбомогц үйлчилнэ.
 */
export const HeroVideo = ({ source, enabled, selected, preload, paused, loop, onEnded, onFail }: HeroVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const controllerRef = useRef<ReturnType<typeof createVideoController> | null>(null)
  const wantRef = useRef<Want>({ enabled, selected, preload, paused })
  const hooksRef = useRef({ onEnded, onFail })
  const [shown, setShown] = useState(false)

  useEffect(() => {
    hooksRef.current = { onEnded, onFail }
  })

  const { hls, mp4, rate } = source

  useEffect(() => {
    const video = videoRef.current

    if (!video) return

    const controller = createVideoController(video, { hls, mp4, rate }, () => wantRef.current, {
      onPlaying: () => setShown(true),
      onEnded: () => hooksRef.current.onEnded(),
      onFail: () => hooksRef.current.onFail()
    })

    controllerRef.current = controller
    controller.sync()

    return () => {
      controller.destroy()
      controllerRef.current = null
    }
  }, [hls, mp4, rate])

  useEffect(() => {
    wantRef.current = { enabled, selected, preload, paused }
    controllerRef.current?.sync()
  }, [enabled, selected, preload, paused])

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      loop={loop}
      preload={selected ? 'auto' : preload ? 'metadata' : 'none'}
      disablePictureInPicture
      disableRemotePlayback
      aria-hidden
      tabIndex={-1}
      className={cn(
        'absolute inset-0 size-full object-cover transition-opacity duration-700',
        shown && enabled ? 'opacity-100' : 'opacity-0'
      )}
    />
  )
}

/**
 * Видео слайдын бичгийн доорх бараан давхарга.
 *
 * ! Градиент нь бичиг байрлах ЗҮҮН ХАГАСТ зургийн слайдын `bg-black/45`-аас
 * ! цайвар биш (цагаан кадр дээр цагаан бичгийн тодрол). Баруун тал нь ил.
 * ! Жижиг дэлгэцэд бичиг бүтэн өргөнөөр тул баруун захад ч бага зэрэг үлдэнэ.
 *
 * ! `gradient` унтраалттай (админы «Бараан градиент») ч видео тоглохгүй
 * ! (хөдөлгөөн багасгах, алдаа) үед зөвхөн постер харагдана — тэр үед
 * ! зургийн слайдын адил `bg-black/45` тавина, эс бөгөөс бичиг уншигдахгүй.
 */
export const HeroVideoOverlay = ({ gradient, posterOnly }: { gradient: boolean; posterOnly: boolean }) =>
  gradient ? (
    <>
      <div className='absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent' />
      <div className='absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20 md:via-black/35 md:to-transparent' />
      <div className='absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/40 to-transparent' />
    </>
  ) : (
    <div
      className={cn(
        'absolute inset-0 bg-black/45 transition-opacity duration-700',
        posterOnly ? 'opacity-100' : 'opacity-0'
      )}
    />
  )
